import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { dispatch } from 'store';
import { setAddOns } from 'actions/expiryRenewalActions';
import GreenCheckboxList, { Option } from 'components/GreenCheckboxList/GreenCheckboxList';
import PopupModal from 'components/PopupModal/PopupModal';
import modalTypes from 'utils/modalTypesEnum';
import { PaidUpgrade } from 'types/expiryRenewal';
import { Store } from 'types/store';
import { resolveUpgradePricing } from '../../utils/pricingUtils';
import AddOnPriceFooter from './AddOnPriceFooter';
import { hasEuropeanRescue, keepAnnual, goMonthly } from '../../utils/europeanRescueMonthlyFlex';
import EuropeanRescueMonthlyFlexModal from './EuropeanRescueMonthlyFlexModal';

// An upgrade dressed for the checkbox list - it keeps its quote so the basket can price it.
type AddOnOption = PaidUpgrade & Option;

type AddOnOptionsProps = {
  additionalUnits?: number;
};

const AddOnOptions = ({ additionalUnits = 0 }: AddOnOptionsProps) => {
  const { addOns, monthlyFlex, pageModel } = useSelector((state: Store) => state?.expiryRenewal);

  // Read straight from Redux so a conflict resolved elsewhere (e.g. the Monthly/Annual toggle) shows up here too.
  const { selected } = addOns;
  const [modalContent, setModalContent] = useState<string | null>(null);
  // The attempted "next" selection, held here until the conflict modal is resolved.
  const [pendingSelection, setPendingSelection] = useState<AddOnOption[] | null>(null);

  const options: AddOnOption[] = (pageModel?.paidUpgrades ?? []).map((item) => ({
    ...item,
    id: item.productCode,
    label: item.title,
    ...(item.longDescription && {
      link: { text: 'Features & exclusions', onClick: () => setModalContent(item.longDescription) },
    }),
    description: `<div class="py-1">${item.shortDescription}</div>`,
    // Price sits below the link, so it travels in the footer slot rather than the description.
    footer: (
      <AddOnPriceFooter
        details={resolveUpgradePricing(item, additionalUnits)}
        context={{
          productCode: item.productCode,
          isMonthlyLongerTermPolicy: pageModel?.isMonthlyLongerTermPolicy,
          monthlyFlex,
          coverPeriod: pageModel?.coverPeriod,
        }}
      />
    ),
  }));

  /**
   * Matched back to the freshly built options by product code rather than passed through from
   * Redux: a selection restored from sessionStorage carries the prices it was stored with, and
   * the tier a quote sits in shifts as members and vehicles are added.
   */
  const selectedOptions = options.filter(
    (option) => selected.some(({ productCode }) => productCode === option.productCode),
  );

  const handleSetSelected = (updater: (_prev: AddOnOption[]) => AddOnOption[]) => {
    const next = updater(selectedOptions);
    const addingEurRescueOnFlex = monthlyFlex && !hasEuropeanRescue(selectedOptions) && hasEuropeanRescue(next);

    if (addingEurRescueOnFlex) {
      setPendingSelection(next);
      return;
    }

    dispatch(setAddOns(next));
  };

  return (
    <>
      <GreenCheckboxList options={options} selected={selectedOptions} setSelected={handleSetSelected} />
      {modalContent && (
        <PopupModal
          resultText={modalContent}
          parseHtml
          modalType={modalTypes.infoModal}
          innerCloseButton
          displayDialogInMiddle
          action={() => setModalContent(null)}
        />
      )}
      {pendingSelection && (
        <EuropeanRescueMonthlyFlexModal
          onDismiss={() => setPendingSelection(null)}
          onKeepAnnual={() => {
            keepAnnual(pendingSelection);
            setPendingSelection(null);
          }}
          onGoMonthly={() => {
            goMonthly(selectedOptions);
            setPendingSelection(null);
          }}
        />
      )}
    </>
  );
};

export default AddOnOptions;
